import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ApiClient, type ExpertType } from "../core/api-client.js";
import { LeadMagnetWidgetBase } from "../core/widget-base.js";
import { widgetStyles } from "../core/widget-styles.js";
import {
  simulationComparaisonFormSchema,
  simulationComparaisonStepSchemas,
} from "../lead-magnets/simulation-comparaison/form-schema.js";
import { mapSimulationComparaisonFormToSubmitPayload } from "../lead-magnets/simulation-comparaison/request-mapper.js";
import {
  getSimulationComparaisonFormProgress,
  getInitialSimulationComparaisonFormStep,
  getNextSimulationComparaisonFormStep,
  getPreviousSimulationComparaisonFormStep,
  type SimulationComparaisonFormModel,
  type SimulationComparaisonRegimeKey,
  type SimulationComparaisonFormStep,
  type SimulationComparaisonResultModel,
  type SimulationComparaisonSwitchInsightModel,
  type SimulationComparaisonYearComparisonModel,
  type SimulationComparaisonWidgetViewState,
} from "../lead-magnets/simulation-comparaison/ui-state.js";

const REGIME_LABELS: Record<string, string> = {
  lmnp_reel: "LMNP au réel",
  lmnp_micro: "LMNP au micro-BIC",
  nu_reel: "Location nue au réel",
};

@customElement("lead-magnet-simulation-comparaison-widget")
export class LeadMagnetSimulationComparaisonWidget extends LeadMagnetWidgetBase {
  @property({ type: String })
  public override slug = "simulation-comparaison";

  @property({ type: String, attribute: "api-base-url" })
  public apiBaseUrl = "";

  @property({ type: String })
  public override source = "landing_simulation_comparaison_widget";

  @state()
  private viewState: SimulationComparaisonWidgetViewState = "idle";

  @state()
  private step: SimulationComparaisonFormStep =
    getInitialSimulationComparaisonFormStep();

  @state()
  private form: SimulationComparaisonFormModel = {
    email: "",
    firstName: "",
    source: "landing_simulation_comparaison_widget",
    consent: false,
    prixAchat: "",
    loyerAnnuel: "",
    slugParameter: "non-resident",
    anneeIndex: "1",
    expertComptable: false,
    courtier: false,
    chasseurImmobilier: false,
    honeypot: "",
  };

  @state()
  private resultData: SimulationComparaisonResultModel | null = null;

  @state()
  private expertMessage = "";

  public static override styles = widgetStyles;

  protected override render() {
    const integrationError = this.getIntegrationError();
    if (integrationError) {
      return html`<p class="error">${integrationError}</p>`;
    }

    const progress = getSimulationComparaisonFormProgress(this.step);
    const isIntroStep = this.step === "intro";
    const isContactStep = this.step === "contact";
    const isResultStep = this.step === "result";
    const hasPrevious = ["bien", "options", "contact", "result"].includes(
      this.step,
    );

    return html`
      <form @submit=${this.onSubmit}>
        ${progress.current > 0
          ? html`<p class="step">
              Étape ${progress.current}/${progress.total}
            </p>`
          : null}
        ${this.renderCurrentStep()}

        <label style="display:none">
          Champ anti-bot
          <input
            name="honeypot"
            type="text"
            tabindex="-1"
            autocomplete="off"
            .value=${this.form.honeypot}
            @input=${(e: Event) => {
              this.form = {
                ...this.form,
                honeypot: (e.target as HTMLInputElement).value,
              };
            }}
          />
        </label>

        ${this.errorMessage
          ? html`<p class="error">${this.errorMessage}</p>`
          : null}
        <div class="actions">
          ${hasPrevious
            ? html`
                <button
                  type="button"
                  class="secondary"
                  ?disabled=${this.isLoading}
                  @click=${this.onPreviousStep}
                >
                  Précédent
                </button>
              `
            : null}
          ${isContactStep
            ? html`<button ?disabled=${this.isLoading}>
                ${this.isLoading
                  ? "Envoi en cours..."
                  : "Recevoir ma comparaison gratuite"}
              </button>`
            : !isResultStep
              ? html`
                  <button
                    type="button"
                    ?disabled=${this.isLoading}
                    @click=${this.onNextStep}
                  >
                    ${isIntroStep ? "Commencer" : "Suivant"}
                  </button>
                `
              : null}
        </div>
      </form>
    `;
  }

  private renderCurrentStep() {
    switch (this.step) {
      case "intro":
        return this.renderIntroStep();
      case "bien":
        return this.renderBienStep();
      case "options":
        return this.renderOptionsStep();
      case "contact":
        return this.renderContactStep();
      case "result":
        return this.renderResultStep();
      default:
        return html``;
    }
  }

  private renderIntroStep() {
    return html`
      <div class="intro-badge">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
        Gratuit - 2 min
      </div>

      <h2 class="intro-title">
        Compare les régimes fiscaux de ton investissement locatif
      </h2>
      <p class="intro-copy">
        Découvre quel régime fiscal est le plus avantageux pour ton projet :
        LMNP au réel, LMNP au micro-BIC ou location nue au réel.
      </p>

      <div class="intro-benefits">
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Comparaison des 3 régimes fiscaux</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Cashflow net d'impôt par régime</span>
        </div>
        <div class="intro-benefit">
          <span class="check">✓</span>
          <span>Meilleur régime identifié automatiquement</span>
        </div>
      </div>

      <div class="intro-footer">
        <span>🔒 Tes données restent confidentielles</span>
      </div>
    `;
  }

  private renderBienStep() {
    return html`
      <label>
        Prix d'achat du bien
        <input
          name="prixAchat"
          type="text"
          inputmode="numeric"
          placeholder="250 000"
          .value=${this.formatThousands(this.form.prixAchat)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "prixAchat",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>
      <label>
        Loyer annuel
        <input
          name="loyerAnnuel"
          type="text"
          inputmode="numeric"
          placeholder="14 400"
          .value=${this.formatThousands(this.form.loyerAnnuel)}
          @input=${(e: Event) =>
            this.updateNumberField(
              "loyerAnnuel",
              (e.target as HTMLInputElement).value,
            )}
        />
      </label>
      <p class="helper">Renseigne le loyer annuel brut (charges comprises).</p>
    `;
  }

  private renderOptionsStep() {
    return html`
      <label>
        Profil fiscal
        <select
          name="slugParameter"
          .value=${this.form.slugParameter}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              slugParameter: (e.target as HTMLSelectElement).value as
                | "resident"
                | "non-resident",
            };
          }}
        >
          <option value="resident">Résident fiscal français</option>
          <option value="non-resident">Non-résident français</option>
        </select>
      </label>
    `;
  }

  private renderContactStep() {
    const prixAchat = this.parseNumber(this.form.prixAchat);
    const loyerAnnuel = this.parseNumber(this.form.loyerAnnuel);
    const rendementBrutEstim =
      prixAchat > 0 ? (loyerAnnuel / prixAchat) * 100 : 0;

    return html`
      <div class="preview-card">
        <div class="preview-row">
          <span class="preview-label">Prix d'achat</span>
          <span class="preview-value">${this.formatCurrency(prixAchat)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Loyer annuel</span>
          <span class="preview-value">${this.formatCurrency(loyerAnnuel)}</span>
        </div>
        <div class="preview-row">
          <span class="preview-label">Rendement brut</span>
          <span class="preview-value"
            >${rendementBrutEstim > 0
              ? `~${rendementBrutEstim.toFixed(1)}%`
              : "—"}</span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Meilleur régime</span>
          <span class="preview-value blurred"
            >████<span class="lock">🔒</span></span
          >
        </div>
        <div class="preview-row">
          <span class="preview-label">Cashflow net d'impôt</span>
          <span class="preview-value blurred"
            >███ €<span class="lock">🔒</span></span
          >
        </div>
      </div>

      <p class="unlock-title">Débloquer ta comparaison fiscale</p>

      <label>
        Email
        <input
          name="email"
          type="email"
          placeholder="ton@email.com"
          .value=${this.form.email}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              email: (e.target as HTMLInputElement).value,
            };
          }}
          required
        />
      </label>

      <label>
        Prénom
        <input
          name="firstName"
          type="text"
          placeholder="Ton prénom"
          .value=${this.form.firstName}
          @input=${(e: Event) => {
            this.form = {
              ...this.form,
              firstName: (e.target as HTMLInputElement).value,
            };
          }}
        />
      </label>

      <label style="display: flex; align-items: center; gap: 8px;">
        <input
          name="consent"
          type="checkbox"
          .checked=${this.form.consent}
          @change=${(e: Event) => {
            this.form = {
              ...this.form,
              consent: (e.target as HTMLInputElement).checked,
            };
          }}
          required
        />
        <span>J'accepte de recevoir le résultat par e-mail.</span>
      </label>

      <div class="reassurance">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
        <span>Données sécurisées - Aucun spam, désabonnement en 1 clic</span>
      </div>
    `;
  }

  private renderResultStep() {
    if (!this.resultData) {
      return html`
        <div class="result-card">
          <p class="result-title">Résultat indisponible</p>
          <p class="helper">
            Impossible d'afficher le résultat pour le moment.
          </p>
        </div>
      `;
    }

    const { commun, regimes, tri, meilleur_regime, bascule_regime } =
      this.resultData;
    const bestLabel = REGIME_LABELS[meilleur_regime] ?? meilleur_regime;

    return html`
      <div class="result-card">
        <p class="result-title">Comparaison des régimes fiscaux</p>

        <div class="preview-card">
          <div class="preview-row">
            <span class="preview-label">Recettes</span>
            <span class="preview-value"
              >${this.formatCurrency(commun.recettes)}</span
            >
          </div>
          <div class="preview-row">
            <span class="preview-label">Rendement brut</span>
            <span class="preview-value"
              >${commun.rendement_brut.toFixed(2)} %</span
            >
          </div>
          <div class="preview-row">
            <span class="preview-label">Rendement net</span>
            <span class="preview-value"
              >${commun.rendement_net.toFixed(2)} %</span
            >
          </div>
          <div class="preview-row">
            <span class="preview-label">Cashflow avant impôt</span>
            <span class="preview-value"
              >${this.formatCurrency(commun.cashflow_avant_impot)}</span
            >
          </div>
        </div>

        <table class="detail-table">
          <thead>
            <tr>
              <th></th>
              <th>LMNP au réel</th>
              <th>LMNP au micro-BIC</th>
              <th>Location nue au réel</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Revenus imposables</td>
              <td>
                ${this.formatCurrency(regimes.lmnp_reel.revenus_imposables)}
              </td>
              <td>
                ${this.formatCurrency(regimes.lmnp_micro.revenus_imposables)}
              </td>
              <td>
                ${this.formatCurrency(regimes.nu_reel.revenus_imposables)}
              </td>
            </tr>
            <tr>
              <td>Impôts</td>
              <td>${this.formatCurrency(regimes.lmnp_reel.impots)}</td>
              <td>${this.formatCurrency(regimes.lmnp_micro.impots)}</td>
              <td>${this.formatCurrency(regimes.nu_reel.impots)}</td>
            </tr>
            <tr>
              <td>Cashflow net</td>
              <td class="${meilleur_regime === "lmnp_reel" ? "positive" : ""}">
                ${this.formatCurrency(regimes.lmnp_reel.cashflow_net_impot)}
              </td>
              <td class="${meilleur_regime === "lmnp_micro" ? "positive" : ""}">
                ${this.formatCurrency(regimes.lmnp_micro.cashflow_net_impot)}
              </td>
              <td class="${meilleur_regime === "nu_reel" ? "positive" : ""}">
                ${this.formatCurrency(regimes.nu_reel.cashflow_net_impot)}
              </td>
            </tr>
          </tbody>
        </table>

        <div
          class="preview-row"
          style="border-top: 2px solid #1674ee; padding-top: 8px; margin-top: 4px;"
        >
          <span class="preview-label" style="font-weight: 700; color: #0f172a;"
            >Meilleur régime</span
          >
          <span class="preview-value" style="font-size: 14px; color: #1674ee;"
            >${bestLabel}</span
          >
        </div>

        ${tri >= 0
          ? html`
              <div class="preview-row">
                <span class="preview-label">TRI</span>
                <span class="preview-value">${tri.toFixed(2)} %</span>
              </div>
            `
          : null}

        <div class="expert-card" style="margin-top: 0.75rem;">
          <span class="expert-eyebrow">Accompagnement</span>
          <p class="expert-title">
            Tu veux avancer avec un expert de confiance spécialisé expatriés ?
          </p>
          <p class="expert-copy">
            Sélectionne les partenaires que tu veux solliciter. Tu peux en
            choisir plusieurs.
          </p>
          <div class="expert-options">
            <label class="expert-option">
              <input
                type="checkbox"
                .checked=${this.form.expertComptable}
                @change=${() => {
                  this.form = {
                    ...this.form,
                    expertComptable: !this.form.expertComptable,
                  };
                }}
              />
              <span>Expert-comptable spécialisé expatriés</span>
            </label>
            <label class="expert-option">
              <input
                type="checkbox"
                .checked=${this.form.courtier}
                @change=${() => {
                  this.form = { ...this.form, courtier: !this.form.courtier };
                }}
              />
              <span>Courtier en financement</span>
            </label>
            <label class="expert-option">
              <input
                type="checkbox"
                .checked=${this.form.chasseurImmobilier}
                @change=${() => {
                  this.form = {
                    ...this.form,
                    chasseurImmobilier: !this.form.chasseurImmobilier,
                  };
                }}
              />
              <span>Chasseur immobilier</span>
            </label>
          </div>
          <button
            type="button"
            ?disabled=${this.isLoading}
            @click=${this.onSubmitExpertOptin}
          >
            ${this.isLoading
              ? "Envoi en cours..."
              : "Être mis en relation avec ces experts"}
          </button>
          ${this.expertMessage
            ? html`<p
                class="expert-feedback ${this.expertMessage.startsWith(
                  "Sélectionne",
                )
                  ? "error"
                  : ""}"
              >
                ${this.expertMessage}
              </p>`
            : null}
          ${this.expertMessage && !this.expertMessage.startsWith("Sélectionne")
            ? html`<button
                type="button"
                class="expert-reset"
                ?disabled=${this.isLoading}
                @click=${this.resetFlow}
              >
                Recommencer
              </button>`
            : null}
        </div>
      </div>
    `;
  }

  private onNextStep = (): void => {
    if (!this.validateStep(this.step)) {
      this.viewState = "error";
      return;
    }
    this.trackStep(this.step);
    this.viewState = "idle";
    this.step = getNextSimulationComparaisonFormStep(this.step);
  };

  private onPreviousStep = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getPreviousSimulationComparaisonFormStep(this.step);
  };

  private onSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault();
    if (this.step !== "contact") {
      this.onNextStep();
      return;
    }

    if (!this.validateStep("contact")) {
      this.viewState = "error";
      return;
    }

    const parsed = simulationComparaisonFormSchema.safeParse(
      this.buildFinalValidationInput(),
    );
    if (!parsed.success) {
      this.viewState = "error";
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return;
    }

    this.startLoading();
    this.viewState = "submitting";

    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      const payload = mapSimulationComparaisonFormToSubmitPayload({
        ...this.form,
        source: parsed.data.source,
        consent: parsed.data.consent,
      });

      const response = await apiClient.submitLeadMagnet(
        "simulation-comparaison",
        payload,
        this.makeIdempotencyKey(),
      );
      this.resultData = this.mapApiResult(response.result.payload);
      this.step = "result";
      this.viewState = "idle";
      this.errorMessage = "";
      this.trackSubmit();
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Soumission impossible.";
      this.viewState = "error";
      this.setError(msg);
      this.trackError(this.step, msg);
    } finally {
      this.stopLoading();
    }
  };

  private validateStep(step: SimulationComparaisonFormStep): boolean {
    if (step === "result") {
      this.errorMessage = "";
      return true;
    }
    const schema = simulationComparaisonStepSchemas[step];
    const parsed = schema.safeParse(this.buildStepValidationInput(step));
    if (!parsed.success) {
      this.setError(parsed.error.issues[0]?.message ?? "Formulaire invalide.");
      return false;
    }
    if (step === "contact") {
      this.form = { ...this.form, source: this.getSourceValue() };
    }
    this.errorMessage = "";
    return true;
  }

  private buildStepValidationInput(
    step: SimulationComparaisonFormStep,
  ): Record<string, unknown> {
    switch (step) {
      case "bien":
        return {
          prixAchat: this.form.prixAchat,
          loyerAnnuel: this.form.loyerAnnuel,
        };
      case "options":
        return {
          slugParameter: this.form.slugParameter,
          anneeIndex: this.form.anneeIndex,
        };
      case "contact":
        return {
          email: this.form.email,
          firstName: this.form.firstName,
          source: this.getSourceValue(),
          consent: this.form.consent,
          honeypot: this.form.honeypot,
        };
      default:
        return {};
    }
  }

  private buildFinalValidationInput(): Record<string, unknown> {
    return { ...this.form, source: this.getSourceValue() };
  }

  private onSubmitExpertOptin = async (): Promise<void> => {
    const expertTypes = this.getExpertTypes();
    if (expertTypes.length === 0) {
      this.expertMessage =
        "Sélectionne au moins un expert pour être mis en relation.";
      return;
    }

    this.startLoading();
    this.errorMessage = "";
    try {
      const apiClient = new ApiClient(this.apiBaseUrl.trim());
      await apiClient.submitExpertOptin("simulation-comparaison", {
        email: this.form.email.trim(),
        firstName: this.form.firstName.trim() || undefined,
        source: this.getSourceValue(),
        expertTypes,
      });
      this.expertMessage =
        expertTypes.length > 0
          ? "Parfait, ta demande a bien été enregistrée."
          : "Merci, ta comparaison est terminée.";
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : "Soumission impossible.";
    } finally {
      this.stopLoading();
    }
  };

  private getExpertTypes(): ExpertType[] {
    const expertTypes: ExpertType[] = [];
    if (this.form.expertComptable) expertTypes.push("expert_comptable");
    if (this.form.courtier) expertTypes.push("courtier");
    if (this.form.chasseurImmobilier) {
      expertTypes.push("chasseur_immobilier");
    }
    return expertTypes;
  }

  private resetFlow = (): void => {
    this.errorMessage = "";
    this.viewState = "idle";
    this.step = getInitialSimulationComparaisonFormStep();
    this.resultData = null;
    this.expertMessage = "";
    this.form = {
      email: "",
      firstName: "",
      source: this.getSourceValue(),
      consent: false,
      prixAchat: "",
      loyerAnnuel: "",
      slugParameter: "non-resident",
      anneeIndex: "1",
      expertComptable: false,
      courtier: false,
      chasseurImmobilier: false,
      honeypot: "",
    };
  };

  private mapApiResult(
    payload: Record<string, unknown>,
  ): SimulationComparaisonResultModel {
    const communRaw =
      typeof payload.commun === "object" && payload.commun !== null
        ? (payload.commun as Record<string, unknown>)
        : {};
    const regimesRaw =
      typeof payload.regimes === "object" && payload.regimes !== null
        ? (payload.regimes as Record<string, Record<string, unknown>>)
        : {};
    const comparaisonsParAnneeRaw = Array.isArray(
      payload.comparaisons_par_annee,
    )
      ? payload.comparaisons_par_annee
      : [];
    const basculeRegimeRaw =
      typeof payload.bascule_regime === "object" &&
      payload.bascule_regime !== null
        ? (payload.bascule_regime as Record<string, unknown>)
        : null;

    return {
      annee: toNumber(payload.annee) ?? 0,
      commun: {
        recettes: toNumber(communRaw.recettes) ?? 0,
        rendement_brut: toNumber(communRaw.rendement_brut) ?? 0,
        charges_exploitation: toNumber(communRaw.charges_exploitation) ?? 0,
        resultat_brut: toNumber(communRaw.resultat_brut) ?? 0,
        rendement_net: toNumber(communRaw.rendement_net) ?? 0,
        charges_financieres: toNumber(communRaw.charges_financieres) ?? 0,
        capital_rembourse: toNumber(communRaw.capital_rembourse) ?? 0,
        cashflow_avant_impot: toNumber(communRaw.cashflow_avant_impot) ?? 0,
      },
      regimes: {
        lmnp_reel: mapRegime(regimesRaw.lmnp_reel),
        lmnp_micro: mapRegime(regimesRaw.lmnp_micro),
        nu_reel: mapRegime(regimesRaw.nu_reel),
      },
      tri: toNumber(payload.tri) ?? 0,
      meilleur_regime: toRegimeKey(payload.meilleur_regime) ?? "lmnp_reel",
      comparaisons_par_annee: comparaisonsParAnneeRaw
        .map((item) => mapYearComparison(item))
        .filter(
          (item): item is SimulationComparaisonYearComparisonModel =>
            item !== null,
        ),
      bascule_regime: mapSwitchInsight(basculeRegimeRaw),
    };
  }

  private makeIdempotencyKey(): string {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return `simcmp_${crypto.randomUUID()}`;
    }
    return `simcmp_${Date.now()}`;
  }

  private getSourceValue(): string {
    return this.source.trim() || this.form.source;
  }

  private updateNumberField(
    field: "prixAchat" | "loyerAnnuel",
    value: string,
  ): void {
    this.form = { ...this.form, [field]: value.replaceAll(/\D+/g, "") };
  }

  private parseNumber(value: string): number {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(value);
  }

  private formatThousands(value: string): string {
    const digits = value.replaceAll(/\D+/g, "").replace(/^0+(?=\d)/, "");
    if (!digits) return "";
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  private getIntegrationError(): string | null {
    if (this.slug !== "simulation-comparaison")
      return 'Ce widget supporte uniquement le slug "simulation-comparaison".';
    const baseUrl = this.apiBaseUrl.trim();
    if (!baseUrl)
      return "Configuration invalide: l'attribut `api-base-url` est obligatoire.";
    try {
      const parsed = new URL(baseUrl);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return "Configuration invalide: `api-base-url` doit utiliser http ou https.";
      }
    } catch {
      return "Configuration invalide: `api-base-url` doit être une URL absolue.";
    }
    return null;
  }
}

function mapRegime(raw: Record<string, unknown> | undefined): {
  revenus_imposables: number;
  impots: number;
  cashflow_net_impot: number;
  cashflow_net_impot_sans_remboursement_capital: number;
} {
  if (!raw) {
    return {
      revenus_imposables: 0,
      impots: 0,
      cashflow_net_impot: 0,
      cashflow_net_impot_sans_remboursement_capital: 0,
    };
  }
  return {
    revenus_imposables: toNumber(raw.revenus_imposables) ?? 0,
    impots: toNumber(raw.impots) ?? 0,
    cashflow_net_impot: toNumber(raw.cashflow_net_impot) ?? 0,
    cashflow_net_impot_sans_remboursement_capital:
      toNumber(raw.cashflow_net_impot_sans_remboursement_capital) ?? 0,
  };
}

function mapYearComparison(
  raw: unknown,
): SimulationComparaisonYearComparisonModel | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const meilleurRegime = toRegimeKey(value.meilleur_regime);
  if (!meilleurRegime) return null;
  return {
    annee_index: toNumber(value.annee_index) ?? 0,
    annee_label: typeof value.annee_label === "string" ? value.annee_label : "",
    annee: toNumber(value.annee) ?? 0,
    meilleur_regime: meilleurRegime,
    tri: toNumber(value.tri) ?? 0,
  };
}

function mapSwitchInsight(
  raw: Record<string, unknown> | null,
): SimulationComparaisonSwitchInsightModel | null {
  if (!raw) return null;
  return {
    message: typeof raw.message === "string" ? raw.message : "",
    premiere_annee_index: toNumber(raw.premiere_annee_index),
    premiere_annee_label:
      typeof raw.premiere_annee_label === "string"
        ? raw.premiere_annee_label
        : null,
    regime_initial: toRegimeKey(raw.regime_initial),
    nouveau_regime: toRegimeKey(raw.nouveau_regime),
  };
}

function toRegimeKey(value: unknown): SimulationComparaisonRegimeKey | null {
  if (value === "lmnp_reel" || value === "lmnp_micro" || value === "nu_reel")
    return value;
  return null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value.replaceAll(/\s+/g, ""));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

declare global {
  interface HTMLElementTagNameMap {
    "lead-magnet-simulation-comparaison-widget": LeadMagnetSimulationComparaisonWidget;
  }
}
